(() => {
  const mobileQuery = window.matchMedia('(max-width: 767px)');
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const products = new WeakMap();
  const instances = new Set();
  const setAttribute = (node, name, value) => {
    if (value === null) {
      if (node.hasAttribute(name)) node.removeAttribute(name);
    } else if (node.getAttribute(name) !== String(value)) {
      node.setAttribute(name, String(value));
    }
  };
  const setClass = (node, name, enabled) => {
    if (node.classList.contains(name) !== enabled) node.classList.toggle(name, enabled);
  };
  const setText = (node, value) => {
    if (node.textContent !== value) node.textContent = value;
  };
  const setHidden = (node, hidden) => {
    if (node.hidden !== hidden) node.hidden = hidden;
  };

  const initialize = (root) => {
    if (products.has(root)) return;
    const selector = root.querySelector('variant-selects');
    const productForm = root.querySelector('product-form');
    const form = productForm?.querySelector('form[data-type="add-to-cart-form"]');
    const submit = form?.querySelector('.add-cart-button[name="add"]');
    if (!form || !submit) return;
    const submitLabel = submit.querySelector(':scope > span');
    const fields = selector ? [...selector.querySelectorAll('fieldset.product-form__input')] : [];
    const fieldStates = fields.map((field) => {
      const swatches = [...field.querySelectorAll('.bls__option-swatch[data-value]')];
      const values = new Set(swatches.map((swatch) => swatch.dataset.value));
      return {
        field,
        swatches,
        multiple: values.size > 1,
        confirmed: values.size <= 1,
        fieldTabindex: field.getAttribute('tabindex'),
        originals: swatches.map((swatch) => ({
          swatch,
          role: swatch.getAttribute('role'),
          tabindex: swatch.getAttribute('tabindex'),
          pressed: swatch.getAttribute('aria-pressed'),
          disabled: swatch.getAttribute('aria-disabled')
        }))
      };
    });
    const assurance = root.querySelector('.lumingem-product-assurance');
    const assuranceHome = assurance ? document.createElement('span') : null;
    if (assuranceHome) {
      assuranceHome.hidden = true;
      assuranceHome.dataset.lumingemAssuranceHome = '';
      assurance.before(assuranceHome);
    }
    const payment = form.querySelector('.bls__payment-button');
    const checkout = payment?.parentElement;
    const paymentWasInert = payment?.hasAttribute('inert') || false;
    const gate = payment ? document.createElement('button') : null;
    if (gate) {
      gate.type = 'button';
      gate.className = 'lumingem-mobile-payment-gate';
      gate.hidden = true;
      gate.textContent = root.dataset.mobilePaymentOptions || 'Select options before checkout';
      payment.after(gate);
    }
    const gallery = root.querySelector('.product__media-mobile .bls__swiper-gallery-mobile');
    const count = gallery ? document.createElement('output') : null;
    if (count) {
      count.className = 'lumingem-mobile-gallery-count';
      count.setAttribute('aria-live', 'polite');
      count.setAttribute('aria-atomic', 'true');
      count.hidden = true;
      gallery.closest('.col_thumb').append(count);
    }

    let frame = 0;
    let nativeLabel = submitLabel?.textContent || '';
    let pendingLabel = '';
    let connectedSwiper = null;
    let priceReview = null;
    let purchaseEntry = null;
    const syncPriceReview = (mobile) => {
      if (!priceReview) {
        if (!mobile) return;
        const details = form.closest('.bls__product-details-infor');
        const price = details?.querySelector(':scope > .bls__product-price');
        const badge = details && [...details.querySelectorAll(':scope > .shopify-app-block')]
          .find((block) => block.querySelector('.jdgm-preview-badge'));
        if (!price || !badge) return;
        const priceHome = document.createElement('span');
        const badgeHome = document.createElement('span');
        const wrapper = document.createElement('div');
        priceHome.hidden = true;
        badgeHome.hidden = true;
        priceHome.dataset.lumingemPriceHome = '';
        badgeHome.dataset.lumingemBadgeHome = '';
        wrapper.className = 'lumingem-mobile-price-review';
        wrapper.hidden = true;
        price.before(priceHome);
        badge.before(badgeHome);
        priceHome.after(wrapper);
        priceReview = { price, badge, priceHome, badgeHome, wrapper };
      }
      const { price, badge, priceHome, badgeHome, wrapper } = priceReview;
      if (mobile) {
        if (!wrapper.isConnected) priceHome.after(wrapper);
        if (price.parentElement !== wrapper) wrapper.append(price);
        if (badge.parentElement !== wrapper) wrapper.append(badge);
      } else {
        if (priceHome.nextElementSibling !== price) priceHome.after(price);
        if (badgeHome.nextElementSibling !== badge) badgeHome.after(badge);
        if (wrapper.isConnected) wrapper.remove();
      }
      setHidden(wrapper, !mobile);
    };
    const missingFields = () => fieldStates.filter((state) => state.multiple && !state.confirmed);
    const pending = () => mobileQuery.matches && missingFields().length > 0;
    const focusMissing = () => {
      const state = missingFields()[0];
      if (!state) return;
      const focusTarget = state.swatches.find((swatch) => !swatch.hasAttribute('data-disabled')) || state.field;
      state.field.scrollIntoView({
        behavior: reducedMotion.matches ? 'instant' : 'smooth',
        block: 'center'
      });
      focusTarget.focus({ preventScroll: true });
    };
    const syncPurchaseEntry = () => {
      const viewport = window.visualViewport;
      const viewportHeight = viewport?.height || window.innerHeight;
      const shortMobile = mobileQuery.matches && viewportHeight <= 740;
      if (!shortMobile) {
        if (purchaseEntry) {
          purchaseEntry.entry.remove();
          setClass(purchaseEntry.breadcrumb, 'lumingem-mobile-purchase-entry', false);
          setClass(purchaseEntry.host, 'lumingem-mobile-entry-host', false);
          setClass(purchaseEntry.host, 'lumingem-mobile-entry-pinned', false);
          purchaseEntry.host.style.removeProperty('--lumingem-mobile-entry-top');
          purchaseEntry.host.style.removeProperty('--lumingem-mobile-entry-reserved-height');
          purchaseEntry = null;
        }
        return;
      }
      if (!purchaseEntry) {
        const breadcrumb = document.querySelector('section.breadcrumbs');
        const container = breadcrumb?.querySelector(':scope > .container');
        if (!breadcrumb || !container) return;
        const host = breadcrumb.closest('.shopify-section') || breadcrumb;
        const entry = document.createElement('div');
        const price = document.createElement('span');
        const button = document.createElement('button');
        entry.className = 'lumingem-mobile-entry';
        price.className = 'lumingem-mobile-entry__price';
        button.type = 'button';
        button.className = 'lumingem-mobile-entry__button';
        entry.append(price, button);
        container.append(entry);
        setClass(breadcrumb, 'lumingem-mobile-purchase-entry', true);
        setClass(host, 'lumingem-mobile-entry-host', true);
        button.addEventListener('click', () => {
          if (pending()) {
            focusMissing();
          } else if (!submit.disabled && !submit.classList.contains('btn-loading')) {
            submit.click();
            schedule();
          }
        });
        purchaseEntry = { breadcrumb, container, host, entry, price, button, reservedHeight: 0 };
      }
      const { breadcrumb, host, entry, price, button } = purchaseEntry;
      const header = document.getElementById('sticky-header');
      const headerBox = header?.getBoundingClientRect();
      const headerVisible = headerBox ? Math.max(0, Math.min(headerBox.height, headerBox.bottom)) : 0;
      const top = headerVisible + 'px';
      if (host.style.getPropertyValue('--lumingem-mobile-entry-top') !== top) {
        host.style.setProperty('--lumingem-mobile-entry-top', top);
      }
      const currentPrice = root.querySelector('.bls__product-price .price__regular .price');
      setText(price, currentPrice?.textContent.trim() || '');
      setHidden(price, !price.textContent);
      setText(button, submitLabel?.textContent.trim() || nativeLabel.trim());
      const isPending = pending();
      const disabled = !isPending && (submit.disabled || submit.classList.contains('btn-loading'));
      if (button.disabled !== disabled) button.disabled = disabled;
      setAttribute(button, 'aria-busy', submit.classList.contains('btn-loading') ? 'true' : null);
      const buttonBox = submit.getBoundingClientRect();
      const viewportTop = viewport?.offsetTop || 0;
      const viewportWidth = viewport?.width || window.innerWidth;
      const clearTop = viewportTop + headerVisible + (purchaseEntry.reservedHeight || breadcrumb.getBoundingClientRect().height);
      const geometricallyVisible = buttonBox.height > 0 && buttonBox.width > 0 &&
        buttonBox.top >= clearTop && buttonBox.bottom <= viewportTop + viewportHeight &&
        buttonBox.left >= 0 && buttonBox.right <= viewportWidth;
      const fullyVisible = geometricallyVisible && [0.15, 0.5, 0.85].every((horizontal) =>
        [0.2, 0.5, 0.8].every((vertical) => {
          const hit = document.elementFromPoint(
            buttonBox.left + buttonBox.width * horizontal,
            buttonBox.top + buttonBox.height * vertical
          );
          return hit === submit || (hit !== null && submit.contains(hit));
        })
      );
      const hostBox = host.getBoundingClientRect();
      if (!host.classList.contains('lumingem-mobile-entry-pinned')) {
        purchaseEntry.reservedHeight = hostBox.height;
      }
      const reservedHeight = purchaseEntry.reservedHeight + 'px';
      if (host.style.getPropertyValue('--lumingem-mobile-entry-reserved-height') !== reservedHeight) {
        host.style.setProperty('--lumingem-mobile-entry-reserved-height', reservedHeight);
      }
      setClass(host, 'lumingem-mobile-entry-pinned', !fullyVisible && hostBox.top < headerVisible);
      setClass(entry, 'lumingem-mobile-entry--redundant', fullyVisible);
      setAttribute(entry, 'aria-hidden', fullyVisible ? 'true' : null);
      setAttribute(entry, 'inert', fullyVisible ? '' : null);
    };
    const syncCounter = () => {
      if (!gallery || !count) return;
      const swiper = gallery.swiper;
      if (swiper && swiper !== connectedSwiper) {
        connectedSwiper?.off?.('slideChange', schedule);
        connectedSwiper?.off?.('slidesLengthChange', schedule);
        connectedSwiper = swiper;
        swiper.on('slideChange', schedule);
        swiper.on('slidesLengthChange', schedule);
      }
      const slides = [...gallery.querySelectorAll(':scope > .swiper-wrapper > .swiper-slide')]
        .filter((slide) => !slide.hidden && getComputedStyle(slide).display !== 'none');
      const active = slides.findIndex((slide) => slide.classList.contains('swiper-slide-active'));
      const current = Math.max(1, Math.min(slides.length, active >= 0 ? active + 1 : (swiper?.realIndex || 0) + 1));
      setText(count, current + ' / ' + slides.length);
      setAttribute(count, 'aria-label', (root.dataset.mobileImageLabel || 'Image') + ' ' + current + ' / ' + slides.length);
      setHidden(count, !mobileQuery.matches || slides.length < 2);
    };
    const sync = () => {
      frame = 0;
      const mobile = mobileQuery.matches;
      const isPending = pending();
      setClass(root, 'lumingem-mobile-purchase', mobile);
      if (selector) setAttribute(selector, 'data-lumingem-pending', isPending ? '' : null);
      setAttribute(productForm, 'data-lumingem-options-pending', isPending ? '' : null);
      syncPriceReview(mobile);

      if (assurance && assuranceHome) {
        if (mobile && productForm.nextElementSibling !== assurance) productForm.after(assurance);
        if (!mobile && assuranceHome.nextElementSibling !== assurance) assuranceHome.after(assurance);
      }

      fieldStates.forEach((state) => {
        const { field, swatches, multiple, confirmed } = state;
        setClass(field, 'lumingem-single-value', mobile && !multiple);
        setAttribute(field, 'data-lumingem-value-count', mobile ? swatches.length : null);
        setAttribute(field, 'data-lumingem-confirmed', mobile && multiple ? String(confirmed) : null);
        setAttribute(field, 'tabindex', mobile ? '-1' : state.fieldTabindex);
        state.originals.forEach((original) => {
          const { swatch } = original;
          if (mobile && multiple) {
            const disabled = swatch.hasAttribute('data-disabled');
            setAttribute(swatch, 'role', 'button');
            setAttribute(swatch, 'tabindex', disabled ? '-1' : '0');
            setAttribute(swatch, 'aria-disabled', String(disabled));
            setAttribute(swatch, 'aria-pressed', String(confirmed && swatch.classList.contains('active')));
          } else {
            setAttribute(swatch, 'role', original.role);
            setAttribute(swatch, 'tabindex', original.tabindex);
            setAttribute(swatch, 'aria-pressed', original.pressed);
            setAttribute(swatch, 'aria-disabled', original.disabled);
          }
        });
        if (mobile && multiple && confirmed && !swatches.some((swatch) => swatch.classList.contains('active'))) {
          state.confirmed = false;
          schedule();
        }
      });

      if (submitLabel) {
        if (submitLabel.textContent !== pendingLabel) nativeLabel = submitLabel.textContent;
        if (isPending) {
          const sizePending = missingFields().some((state) =>
            state.field.classList.contains('lumingem-ring-size') ||
            /size|尺码|尺寸/i.test(state.field.dataset.lumingemOptionName || '')
          );
          pendingLabel = sizePending
            ? (root.dataset.mobileChooseSize || 'Choose size')
            : (root.dataset.mobileSelectOptions || 'Select options');
          setText(submitLabel, pendingLabel);
        } else if (pendingLabel && submitLabel.textContent === pendingLabel) {
          setText(submitLabel, nativeLabel);
          pendingLabel = '';
        }
      }

      if (payment && gate) {
        setAttribute(payment, 'inert', isPending || paymentWasInert ? '' : null);
        const box = payment.getBoundingClientRect();
        const parentBox = checkout.getBoundingClientRect();
        const showGate = isPending && box.width > 0 && box.height > 0;
        setHidden(gate, !showGate);
        if (showGate) {
          const geometry = {
            top: box.top - parentBox.top + checkout.scrollTop,
            left: box.left - parentBox.left + checkout.scrollLeft,
            width: box.width,
            height: box.height
          };
          Object.entries(geometry).forEach(([key, value]) => {
            const cssValue = value + 'px';
            if (gate.style[key] !== cssValue) gate.style[key] = cssValue;
          });
        }
      }
      syncCounter();
      syncPurchaseEntry();
    };
    function schedule() {
      if (!frame) frame = requestAnimationFrame(sync);
    }
    const confirmSwatch = (swatch) => {
      const state = fieldStates.find((entry) => entry.field.contains(swatch));
      if (!state?.multiple || swatch.hasAttribute('data-disabled')) return;
      state.confirmed = true;
      schedule();
    };
    root.addEventListener('click', (event) => {
      if (!pending() || !event.target.closest('.add-cart-button[name="add"]')) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      focusMissing();
    }, true);
    root.addEventListener('submit', (event) => {
      if (event.target !== form || !pending()) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      focusMissing();
    }, true);
    root.addEventListener('click', (event) => {
      if (!mobileQuery.matches || !event.isTrusted) return;
      const swatch = event.target.closest('.bls__option-swatch[data-value]');
      if (swatch && selector?.contains(swatch)) confirmSwatch(swatch);
    });
    root.addEventListener('keydown', (event) => {
      if (!mobileQuery.matches || !event.isTrusted || !['Enter', ' '].includes(event.key)) return;
      const swatch = event.target.closest('.bls__option-swatch[data-value]');
      if (!swatch || !selector?.contains(swatch) || swatch.hasAttribute('data-disabled')) return;
      event.preventDefault();
      swatch.click();
      confirmSwatch(swatch);
    });
    gate?.addEventListener('click', focusMissing);
    root.addEventListener('click', (event) => {
      if (!purchaseEntry || !event.target.closest('.lumingem-product-assurance__materials-link')) return;
      requestAnimationFrame(() => requestAnimationFrame(() => requestAnimationFrame(() => {
        if (!purchaseEntry || !mobileQuery.matches || (window.visualViewport?.height || window.innerHeight) > 740) return;
        const tab = document.querySelector('a[data-block-id="proTabslumingem_cro_care"]');
        const panel = document.getElementById('proTabslumingem_cro_care');
        const tabs = tab?.closest('.product.info.detailed') || panel?.closest('.product.info.detailed');
        if (!tabs) return;
        const headerBox = document.getElementById('sticky-header')?.getBoundingClientRect();
        const headerVisible = headerBox ? Math.max(0, Math.min(headerBox.height, headerBox.bottom)) : 0;
        const entryHeight = purchaseEntry.reservedHeight || purchaseEntry.host.getBoundingClientRect().height;
        const top = tabs.getBoundingClientRect().top + window.scrollY - headerVisible - entryHeight - 12;
        window.scrollTo({ top: Math.max(0, top), behavior: reducedMotion.matches ? 'instant' : 'smooth' });
        schedule();
      })));
    });

    new MutationObserver(schedule).observe(root, {
      childList: true,
      subtree: true,
      characterData: true,
      attributes: true,
      attributeFilter: ['class', 'disabled', 'data-disabled']
    });
    if (payment) new ResizeObserver(schedule).observe(payment);
    new IntersectionObserver(schedule, { threshold: [0, 1] }).observe(submit);
    const header = document.getElementById('sticky-header');
    if (header) new ResizeObserver(schedule).observe(header);
    window.addEventListener('resize', schedule, { passive: true });
    window.addEventListener('scroll', schedule, { passive: true });
    window.visualViewport?.addEventListener('resize', schedule, { passive: true });
    window.visualViewport?.addEventListener('scroll', schedule, { passive: true });
    const instance = { root, schedule };
    products.set(root, instance);
    instances.add(instance);
    sync();
  };

  const start = () => {
    document.querySelectorAll('.bls__template-product').forEach(initialize);
  };
  mobileQuery.addEventListener('change', () => instances.forEach((instance) => instance.schedule()));
  document.addEventListener('shopify:section:load', start);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();
